/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 71 lines
 * - Adding: ~4 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SPLIT_COMPANY_PROFILE_HOOK_REFINED
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Refined CompanyProfileForm hook to meet line limits by extracting sync logic.
 * Fixed incorrect relative import paths and type errors.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/company-profile/hooks/useCompanyForm.ts
 */
import { useRef } from "react";
import { useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSetup } from "../../../Setup";
import { notify } from "../../../../utils/notifications";
import { normalizeOptionalFields, COMPANY_OPTIONAL_FIELDS } from "../../../../utils/formHelpers";
import { companyValidationSchema } from "../utils/companyValidation";
import { useCompanySync } from "./useCompanySync";
import { User } from "../../../../interfaces/User";

export const DEFAULT_HOURS = {
  fromDay: "Monday" as const,
  toDay: "Friday" as const,
  from: "9 AM",
  to: "5 PM",
  timeZone: "America/New_York",
};

export const useCompanyForm = () => {
  const { profile, loading, save, setDirty } = useSetup();
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const nextSectionRef = useRef<string | null>(null);

  const formik = useFormik({
    initialValues: {
      companyName: "", website: "", companyEmail: "", phone: "", address1: "", address2: "",
      city: "", state: "", zip: "", country: "", companyLinkedIn: "", companyX: "",
      companyInstagram: "", companyFacebook: "", fromDay: DEFAULT_HOURS.fromDay,
      toDay: DEFAULT_HOURS.toDay, from: DEFAULT_HOURS.from, to: DEFAULT_HOURS.to,
      services: "", aboutCompany: "", companyLogos: [] as any[], certifications: [] as any[], timeZone: DEFAULT_HOURS.timeZone,
    },
    validationSchema: companyValidationSchema,
    onSubmit: async (values) => {
      const submitData = normalizeOptionalFields({
        ...values, telCompany: values.phone,
        hours: values.fromDay && values.toDay && values.from && values.to && values.timeZone
          ? { fromDay: values.fromDay, toDay: values.toDay, from: values.from, to: values.to, timeZone: values.timeZone }
          : undefined,
      }, COMPANY_OPTIONAL_FIELDS);
      try {
        await save(submitData as Partial<User>);
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.success("Company profile updated successfully");
        setDirty(false);
        if (nextSectionRef.current === "reviewcompany") navigate("/setup/reviewcompany");
        else if (nextSectionRef.current) setSearchParams({ section: nextSectionRef.current });
      } catch (error) {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(error, "We couldn't update your company profile. Please try again");
      }
    },
  });

  const { dbPresence } = useCompanySync(loading, profile, formik, setDirty);

  return { formik, profile, loading, dbPresence, nextSectionRef };
};
